import App from 'ClientPortal/src/App'
import invariant from 'tiny-invariant'
import { AuthenticatedApp, setAmplifyAuthLanguage } from 'ComponentLibrary'
import { useEffect, useState } from 'react'
import { initI18n, i18n } from 'ClientPortal/src/i18n/init'
import { DEFAULT_LOCALE, getBrowserLocale } from 'ClientPortal/src/i18n/supportedLocales'
import { useTranslation } from 'react-i18next'
import { readPersistedLocale } from 'ClientPortal/src/i18n/persistedLocale'

export function AuthenticatedAppWrapper() {
    const { error, isReady } = useInitialAuthLocale()

    if (error) {
        throw error
    }

    if (!isReady) {
        return null
    }

    return <AuthenticatedAppWrapperContent />
}

function AuthenticatedAppWrapperContent() {
    const { t, i18n: i18nInstance } = useTranslation('pages', { keyPrefix: 'authenticatedApp' })

    useEffect(() => {
        setAmplifyAuthLanguage(i18nInstance.resolvedLanguage)
    }, [i18nInstance.resolvedLanguage])

    return (
        <AuthenticatedApp
            authLogoSrc="/einwertLogoWhite.png"
            authBackgroundSrc="/einwertLoginBackground.jpg"
            authSubtitle={t('subtitle')}
            translations={{
                supportNote: {
                    prefix: t('supportNote.prefix'),
                    suffix: t('supportNote.suffix'),
                },
                formFields: {
                    email: t('formFields.email'),
                    password: t('formFields.password'),
                    code: t('formFields.code'),
                    newPassword: t('formFields.newPassword'),
                    confirmNewPassword: t('formFields.confirmNewPassword'),
                },
                errors: {
                    signIn: t('errors.signIn'),
                    forgotPasswordSubmit: t('errors.forgotPasswordSubmit'),
                    confirmSignIn: t('errors.confirmSignIn'),
                },
                signIn: {
                    title: t('signIn.title'),
                    description: t('signIn.description'),
                    forgotPassword: t('signIn.forgotPassword'),
                },
                forgotPassword: {
                    title: t('forgotPassword.title'),
                    description: t('forgotPassword.description'),
                },
                confirmResetPassword: {
                    title: t('confirmResetPassword.title'),
                    description: t('confirmResetPassword.description'),
                },
                forceNewPassword: {
                    title: t('forceNewPassword.title'),
                    description: t('forceNewPassword.description'),
                    minLengthError: t('forceNewPassword.minLengthError'),
                    mismatchError: t('forceNewPassword.mismatchError'),
                },
            }}
        >
            {({ signOut }) => {
                invariant(signOut, 'signOut and user must be defined')
                return <App signOut={signOut} />
            }}
        </AuthenticatedApp>
    )
}

function useInitialAuthLocale() {
    const [isReady, setIsReady] = useState(i18n.isInitialized)
    const [error, setError] = useState<unknown>()

    useEffect(() => {
        if (i18n.isInitialized) {
            setAmplifyAuthLanguage(i18n.resolvedLanguage)
            setIsReady(true)
            return
        }

        let isMounted = true
        const persistedLocale = readPersistedLocale()

        void initI18n(persistedLocale ?? getBrowserLocale() ?? DEFAULT_LOCALE)
            .then(() => {
                setAmplifyAuthLanguage(i18n.resolvedLanguage)
                if (isMounted) {
                    setError(undefined)
                    setIsReady(true)
                }
            })
            .catch((initializationError) => {
                if (isMounted) {
                    setError(initializationError)
                }
            })

        return () => {
            isMounted = false
        }
    }, [])

    return { error, isReady }
}
